/**
 * Employer Quick Wins Dashboard
 *
 * Shows immediate value opportunities:
 * - Low-hanging fruit cards (employees who would benefit most)
 * - One-click outreach capabilities
 * - Impact calculator
 * - Success stories
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import {
  Target,
  TrendingUp,
  DollarSign,
  Users,
  Zap,
  Gift,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Send,
  Calculator,
  Trophy,
  ChevronRight,
  Loader2,
  Star,
  Heart,
  Clock,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { useTabbedChrome } from '@/components/weavein';

interface QuickWinOpportunity {
  id: string;
  type: 'high_impact' | 'easy_win' | 'urgent' | 'trending';
  title: string;
  description: string;
  employeeCount: number;
  potentialSavings: number;
  confidence: number;
  programId?: number;
  programName?: string;
  actionLabel: string;
  priority: number;
}

interface ImpactMetrics {
  totalPotentialSavings: number;
  employeesAtRisk: number;
  recommendedActions: number;
  averageROI: number;
}

interface SuccessStory {
  id: string;
  industry: string;
  companySize: string;
  challenge: string;
  solution: string;
  result: string;
  savingsPercent: number;
  timeframe: string;
}

interface EmployerQuickWinsProps {
  employerId: number;
  onNavigate?: (tab: string) => void;
}

export default function EmployerQuickWins({ employerId, onNavigate }: EmployerQuickWinsProps) {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState<QuickWinOpportunity[]>([]);
  const [metrics, setMetrics] = useState<ImpactMetrics>({
    totalPotentialSavings: 0,
    employeesAtRisk: 0,
    recommendedActions: 0,
    averageROI: 0,
  });
  const [selectedOpportunity, setSelectedOpportunity] = useState<QuickWinOpportunity | null>(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorInputs, setCalculatorInputs] = useState({
    avgSalary: 65000,
    turnoverCost: 0.5, // 50% of salary
    atRiskCount: 0,
    engagementRate: 0.7, // 70% expected engagement
  });

  // Success stories (in production, these would come from an API)
  const successStories: SuccessStory[] = [
    {
      id: '1',
      industry: 'Healthcare',
      companySize: '500-1000 employees',
      challenge: 'High turnover in nursing staff due to burnout and childcare challenges',
      solution: 'Implemented emergency childcare support and mental wellness programs',
      result: 'Reduced nursing turnover by 23% in 6 months',
      savingsPercent: 23,
      timeframe: '6 months',
    },
    {
      id: '2',
      industry: 'Technology',
      companySize: '200-500 employees',
      challenge: 'Remote employees feeling disconnected, leading to disengagement',
      solution: 'Launched virtual wellness community and flexible benefits stipend',
      result: 'Improved retention rate by 18% and engagement scores by 31%',
      savingsPercent: 18,
      timeframe: '4 months',
    },
    {
      id: '3',
      industry: 'Manufacturing',
      companySize: '1000+ employees',
      challenge: 'Financial stress impacting productivity and attendance',
      solution: 'Introduced financial wellness coaching and emergency assistance fund',
      result: 'Reduced absenteeism by 15% and saved $1.2M annually',
      savingsPercent: 15,
      timeframe: '12 months',
    },
  ];

  // Fetch quick win opportunities
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch retention analysis
        const retentionUrl = getWeaveinApiUrl(`/api/employer/${employerId}/retention-risk`);
        const retentionResponse = await authFetch(retentionUrl);

        if (retentionResponse.ok) {
          const data = await retentionResponse.json();

          // Generate opportunities based on data
          const generatedOpportunities: QuickWinOpportunity[] = [];

          // High-risk employees opportunity
          if (data.high_risk_count > 0) {
            generatedOpportunities.push({
              id: 'high-risk',
              type: 'urgent',
              title: 'Address High-Risk Employees',
              description: `${data.high_risk_count} employees show elevated turnover risk. Early intervention can significantly improve retention.`,
              employeeCount: data.high_risk_count,
              potentialSavings: data.high_risk_count * 32500, // Average cost per turnover
              confidence: 85,
              actionLabel: 'View Action Plans',
              priority: 1,
            });
          }

          // Quick engagement wins
          if (data.total_employees > 0) {
            const disengagedCount = Math.floor(data.total_employees * 0.15);
            if (disengagedCount > 0) {
              generatedOpportunities.push({
                id: 'engagement-boost',
                type: 'easy_win',
                title: 'Boost Low Engagement',
                description: `${disengagedCount} employees have low engagement scores. Simple outreach can improve sentiment.`,
                employeeCount: disengagedCount,
                potentialSavings: disengagedCount * 8000,
                confidence: 72,
                actionLabel: 'Send Outreach',
                priority: 2,
              });
            }
          }

          // Program enrollment opportunity
          generatedOpportunities.push({
            id: 'program-enrollment',
            type: 'high_impact',
            title: 'Increase Program Enrollment',
            description: 'Only 34% of eligible employees are enrolled in support programs. Higher enrollment correlates with 40% better retention.',
            employeeCount: Math.floor((data.total_employees || 100) * 0.66),
            potentialSavings: Math.floor((data.total_employees || 100) * 0.66) * 5000,
            confidence: 78,
            actionLabel: 'Launch Campaign',
            priority: 3,
          });

          // Benefits awareness
          generatedOpportunities.push({
            id: 'benefits-awareness',
            type: 'easy_win',
            title: 'Benefits Awareness Campaign',
            description: 'Survey data shows 45% of employees are unaware of available benefits. Awareness drives utilization.',
            employeeCount: Math.floor((data.total_employees || 100) * 0.45),
            potentialSavings: Math.floor((data.total_employees || 100) * 0.45) * 2500,
            confidence: 65,
            actionLabel: 'Create Campaign',
            priority: 4,
          });

          // Trending: Mental health
          generatedOpportunities.push({
            id: 'mental-health',
            type: 'trending',
            title: 'Mental Health Support',
            description: 'Industry trend: Companies investing in mental health see 4x ROI. Consider expanding EAP services.',
            employeeCount: data.total_employees || 100,
            potentialSavings: (data.total_employees || 100) * 3000,
            confidence: 70,
            programName: 'Mental Health & Wellness',
            actionLabel: 'Explore Programs',
            priority: 5,
          });

          setOpportunities(generatedOpportunities);

          // Set metrics
          setMetrics({
            totalPotentialSavings: generatedOpportunities.reduce((sum, o) => sum + o.potentialSavings, 0),
            employeesAtRisk: data.high_risk_count || 0,
            recommendedActions: generatedOpportunities.length,
            averageROI: 340, // Industry average
          });

          setCalculatorInputs(prev => ({
            ...prev,
            atRiskCount: data.high_risk_count || 0,
          }));
        }
      } catch (error) {
        console.error('Error fetching quick wins data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (employerId) {
      fetchData();
    }
  }, [employerId, token]);

  // Calculate ROI
  const calculatedROI = useMemo(() => {
    const turnoverCostPerEmployee = calculatorInputs.avgSalary * calculatorInputs.turnoverCost;
    const potentialLoss = calculatorInputs.atRiskCount * turnoverCostPerEmployee;
    const expectedSavings = potentialLoss * calculatorInputs.engagementRate;
    const programCost = calculatorInputs.atRiskCount * 500; // Estimated per-employee program cost
    const netSavings = expectedSavings - programCost;
    const roi = programCost > 0 ? (netSavings / programCost) * 100 : 0;

    return {
      turnoverCostPerEmployee,
      potentialLoss,
      expectedSavings,
      programCost,
      netSavings,
      roi,
    };
  }, [calculatorInputs]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'urgent':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'high_impact':
        return <Target className="w-5 h-5 text-purple-500" />;
      case 'easy_win':
        return <Zap className="w-5 h-5 text-amber-500" />;
      case 'trending':
        return <TrendingUp className="w-5 h-5 text-blue-500" />;
      default:
        return <Star className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'urgent':
        return <Badge className="bg-red-100 text-red-700">Urgent</Badge>;
      case 'high_impact':
        return <Badge className="bg-purple-100 text-purple-700">High Impact</Badge>;
      case 'easy_win':
        return <Badge className="bg-amber-100 text-amber-700">Easy Win</Badge>;
      case 'trending':
        return <Badge className="bg-blue-100 text-blue-700">Trending</Badge>;
      default:
        return <Badge variant="secondary">Opportunity</Badge>;
    }
  };

  const handleNavigate = (tab: string) => {
    if (onNavigate) {
      onNavigate(tab);
    } else {
      const event = new CustomEvent('changeTab', { detail: tab });
      window.dispatchEvent(event);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">{t('employer.quickWins.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          {!tabbed && (
          <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          )}
          <div>
            {!tabbed && (
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {t('employer.quickWins.title')}
            </h1>
            )}
            <p className="text-gray-600 dark:text-gray-400">
              {t('employer.quickWins.subtitle')}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowCalculator(!showCalculator)}
          className="flex items-center gap-2"
        >
          <Calculator className="w-4 h-4" />
          {t('employer.quickWins.roiCalculator')}
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="h-5 w-5 text-green-500" />
              <span className="text-sm text-muted-foreground">{t('employer.quickWins.potentialSavings')}</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              ${(metrics.totalPotentialSavings / 1000).toFixed(0)}K
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span className="text-sm text-muted-foreground">{t('employer.quickWins.atRiskEmployees')}</span>
            </div>
            <div className="text-2xl font-bold text-amber-600">
              {metrics.employeesAtRisk}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-purple-500" />
              <span className="text-sm text-muted-foreground">{t('employer.quickWins.actionsAvailable')}</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {metrics.recommendedActions}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-blue-500" />
              <span className="text-sm text-muted-foreground">{t('employer.quickWins.avgIndustryRoi')}</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {metrics.averageROI}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROI Calculator */}
      {showCalculator && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              {t('employer.quickWins.roiCalculator')}
            </CardTitle>
            <CardDescription>
              {t('employer.quickWins.roiCalculatorDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-6">
              {/* Inputs */}
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('employer.quickWins.avgEmployeeSalary')}
                  </label>
                  <input
                    type="number"
                    value={calculatorInputs.avgSalary}
                    onChange={(e) => setCalculatorInputs(prev => ({
                      ...prev,
                      avgSalary: parseInt(e.target.value) || 0,
                    }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('employer.quickWins.turnoverCost')}
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={calculatorInputs.turnoverCost * 100}
                    onChange={(e) => setCalculatorInputs(prev => ({
                      ...prev,
                      turnoverCost: (parseFloat(e.target.value) || 0) / 100,
                    }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('employer.quickWins.atRiskEmployees')}
                  </label>
                  <input
                    type="number"
                    value={calculatorInputs.atRiskCount}
                    onChange={(e) => setCalculatorInputs(prev => ({
                      ...prev,
                      atRiskCount: parseInt(e.target.value) || 0,
                    }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('employer.quickWins.expectedEngagementRate')}
                  </label>
                  <input
                    type="number"
                    value={calculatorInputs.engagementRate * 100}
                    onChange={(e) => setCalculatorInputs(prev => ({
                      ...prev,
                      engagementRate: (parseFloat(e.target.value) || 0) / 100,
                    }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  {t('employer.quickWins.projectedResults')}
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('employer.quickWins.costPerTurnover')}:</span>
                    <span className="font-medium">${calculatedROI.turnoverCostPerEmployee.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('employer.quickWins.potentialLoss')}:</span>
                    <span className="font-medium text-red-600">${calculatedROI.potentialLoss.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('employer.quickWins.programInvestment')}:</span>
                    <span className="font-medium">${calculatedROI.programCost.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">{t('employer.quickWins.expectedSavings')}:</span>
                    <span className="font-medium text-green-600">${calculatedROI.expectedSavings.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{t('employer.quickWins.netRoi')}:</span>
                      <span className="text-2xl font-bold text-green-600">{calculatedROI.roi.toFixed(0)}%</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-gray-600 dark:text-gray-400">{t('employer.quickWins.netSavings')}:</span>
                      <span className="font-bold text-green-600">${calculatedROI.netSavings.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Opportunities List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-purple-600" />
            {t('employer.quickWins.recommendedActions')}
          </CardTitle>
          <CardDescription>
            {t('employer.quickWins.recommendedActionsDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {opportunities.map((opportunity) => (
            <div
              key={opportunity.id}
              className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
            >
              <div className="flex items-start gap-4">
                <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                  {getTypeIcon(opportunity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                      {opportunity.title}
                    </h4>
                    {getTypeBadge(opportunity.type)}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {opportunity.description}
                  </p>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span>{opportunity.employeeCount} employees</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 font-medium">
                        ${(opportunity.potentialSavings / 1000).toFixed(0)}K potential
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BarChart3 className="w-4 h-4 text-blue-500" />
                      <span>{opportunity.confidence}% confidence</span>
                    </div>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleNavigate('employee-retention')}
                  className="flex-shrink-0"
                >
                  {opportunity.actionLabel}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Success Stories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            {t('employer.quickWins.successStories')}
          </CardTitle>
          <CardDescription>
            {t('employer.quickWins.successStoriesDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {successStories.map((story) => (
              <div
                key={story.id}
                className="p-4 rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="secondary">{story.industry}</Badge>
                  <span className="text-xs text-gray-500">{story.companySize}</span>
                </div>
                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2 text-sm">
                  {story.challenge}
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                  <strong>Solution:</strong> {story.solution}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-1 text-green-600">
                    <TrendingUp className="w-4 h-4" />
                    <span className="font-bold">{story.savingsPercent}%</span>
                  </div>
                  <div className="flex items-center gap-1 text-gray-500 text-xs">
                    <Clock className="w-3 h-3" />
                    <span>{story.timeframe}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* CTA Section */}
      <Card className="bg-gradient-to-r from-purple-600 to-blue-600 border-0">
        <CardContent className="py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-white">
            <div>
              <h3 className="text-xl font-bold mb-1">{t('employer.quickWins.readyToCapture')}</h3>
              <p className="text-purple-100">
                {t('employer.quickWins.startWithRecommended')}
              </p>
            </div>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => handleNavigate('employee-retention')}
              className="bg-white text-purple-600 hover:bg-purple-50"
            >
              {t('employer.quickWins.getStarted')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
